import { Autocomplete, Box, SvgIcon, FormControl, Grid, TextField, Chip } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import SearchIcon from "@mui/icons-material/Search";
import { useSelector } from "react-redux";
import { arrowDown, copyIcon } from "../../../../Assets/Svg/Svg";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Checkbox from "@mui/material/Checkbox";
import CopySummaryModal from "./CopySummaryModal";
import FroalaEditorComponent from "../../../Common/HtmlEditor/FroalaEditorComponent";
import uuid from "react-uuid";
import { getTextColor } from "../../../Helpers/HelperFunctions";
import axios from "axios";
import { origin } from "../../../Config/Api-constants";
import { Base64 } from "js-base64";
import {
  getTagChipVisibility,
  mergeMuiOutlinedInputRef,
  useTagFieldCollapsedVisibleCount,
} from "./tagAutocompleteFieldUtils";

const baseFroalaConfig = {
  imagePaste: false,
  spellCheck: true,
  heightMin: 127,
  heightMax: 201,
  linkAutoPrefix: 'https://',
  linkAlwaysBlank: true,
  toolbarButtons: {
    moreText: {
      buttons: ['bold', 'italic', 'underline', 'fontFamily', 'fontSize', 'textColor', 'insertLink'],
      align: 'left',
      buttonsVisible: 7
    },
    moreRich: {
      buttons: ['backgroundColor', 'alignLeft', 'alignCenter', 'alignRight', 'formatOLSimple', 'formatUL', 'outdent', 'indent', 'spellChecker', 'selectAll', 'html', 'clearFormatting'],
      align: 'left',
      buttonsVisible: 0
    },
    moreMisc: { buttons: ['undo', 'redo'], align: 'right', buttonsVisible: 2 }
  },
  toolbarButtonsXS: [['bold', 'italic', 'underline'], ['fontFamily', 'fontSize'], ['insertLink', 'undo', 'redo']],
  pluginsEnabled: ['wordPaste', 'spellChecker', 'codeBeautifier', 'fontFamily', 'fontSize', 'colors', 'align', 'lists', 'paragraphFormat', 'link']
};

const getFroalaConfig = (showAI) => {
  const config = JSON.parse(JSON.stringify(baseFroalaConfig)); // deep copy to avoid mutation
  if (showAI) {
    const miscButtons = config.toolbarButtons.moreMisc.buttons;
    if (!miscButtons.includes('aiHelper')) {
      config.toolbarButtons.moreMisc.buttons = ['aiHelper', ...miscButtons];
    }
    config.toolbarButtons.moreMisc.buttonsVisible = Math.max(config.toolbarButtons.moreMisc.buttonsVisible, 3);
  }
  return config;
};

function CLAddCommentModal(props) {
  const { categoryName, categoryNameFromLocation, commentTitle, setCommentTitle, commentTag, setCommentTag, commentSummaryTag, setCommentSummaryTag, commentText, setCommentText, commentSummary, setCommentSummary, categoryId, prefetchedTagList, selectedFilterTagID } = props;
  const storeData = useSelector((state) => state.CommonReducer);
  const [open, setOpen] = useState(false);
  const [summaryCopyModalOpen, setSummaryCopyModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const [tagsInputFocused, setTagsInputFocused] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [activeEditorId, setActiveEditorId] = useState("");
  const [commentOptionList, setCommentOptionList] = useState([]);
  const [tag, setTag] = useState(storeData?.clTag !== undefined ? storeData?.clTag : []);
  const [summaryTag, setSummaryTag] = useState(storeData?.clSummaryTag !== undefined ? storeData?.clSummaryTag : []);
  const initialTagPrefillDoneRef = useRef(false);
  const editor = useRef();
  const commentEditor = useRef();

  useEffect(() => {
    if (Array.isArray(prefetchedTagList) && prefetchedTagList.length > 0) {
      setTag(prefetchedTagList);
    }
  }, [prefetchedTagList]);

  useEffect(() => {
    if (Array.isArray(prefetchedTagList) && prefetchedTagList.length > 0) return;
    const getTagList = async () => {
      let baseUrl = await origin();
      let sessionAPIKey = sessionStorage.getItem("CK");
      let sessionSecretKey = sessionStorage.getItem("SK");
      axios.get(`${baseUrl}tags`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Base64.decode(sessionSecretKey)}`,
          "api-key": `${Base64.decode(sessionAPIKey)}`,
          platform: "web",
          "api-platform": "WRW",
        },
      }).then(res => {
        setTag(res.data.data || []);
      }).catch(err => console.log('err', err));
    }
    getTagList();
  }, [prefetchedTagList]);

  useEffect(() => {
    if (initialTagPrefillDoneRef.current) return;
    if (!Array.isArray(selectedFilterTagID) || selectedFilterTagID.length === 0) return;
    if (Array.isArray(commentTag) && commentTag.length > 0) return;

    // Use already loaded CL tags first for immediate UI feedback;
    // fall back to fetched tags list when available.
    const fastSourceTags = Array.isArray(storeData?.clTag) ? storeData.clTag : [];
    const availableTags = (tag && tag.length > 0) ? tag : fastSourceTags;
    if (!Array.isArray(availableTags) || availableTags.length === 0) return;

    const selectedFilterIds = selectedFilterTagID.map((id) => String(id));
    const defaultSelectedTags = availableTags.filter((item) => selectedFilterIds.includes(String(item?.id)));

    if (defaultSelectedTags.length > 0) {
      setCommentTag(defaultSelectedTags);
      initialTagPrefillDoneRef.current = true;
    }
  }, [selectedFilterTagID, tag, commentTag, setCommentTag, storeData?.clTag]);

  const handleCopyButton = () => {
    if (commentText?.innerHTML?.length === 0 || commentText?.innerHTML === "<p><br></p>") {
      return null;
    } else if (commentSummary?.innerHTML?.length === 0 || commentSummary?.innerHTML === "<p><br></p>") {
      handleCopyComment();
    } else {
      setSummaryCopyModalOpen(true);
    }
  };

  function handleCopyComment() {
    let newCommentText = commentText?.innerHTML;
    let updatedText = newCommentText;
    updatedText = updatedText.replace(/name="([^"]*?)comment([^"]*?)"/g, 'name="$1summary$2"');
    const newCommentDropdownList = commentText?.dropdownList?.map((dropdown) => {
      const uniqueId = uuid();
      const regex = new RegExp(dropdown?.dropdown_uuid, "g");
      updatedText = updatedText.replace(regex, uniqueId);
      return {
        ...dropdown,
        dropdown_uuid: uniqueId,
        name: dropdown?.name || "",
        selected_value: dropdown?.selected_value,
      };
    });
    newCommentText = updatedText;
    setCommentSummary((prevState) => ({ ...prevState, innerHTML: newCommentText, dropdownList: newCommentDropdownList }));
    setSummaryCopyModalOpen(false);
    const editorInstance = editor?.current?.editor;
    editorInstance?.html?.set(newCommentText)
    setCopied(true);
  }

  const handleOpen = (e) => {
    e.stopPropagation();
    setOpen(true);
  };
  const handleTagOpen = (e) => {
    e.stopPropagation();
    setTagOpen(true);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setOpen(false);
  };

  const handleTagClose = (e) => {
    e.stopPropagation();
    setTagOpen(false);
  };

  const tagAutocompleteValue = useMemo(
    () =>
      (tag || []).filter((tagItem) =>
        (commentTag || []).some((selectedTag) => String(selectedTag?.id) === String(tagItem?.id))
      ),
    [tag, commentTag]
  );

  const { tagsInputRootRef, collapsedVisibleTagCount } = useTagFieldCollapsedVisibleCount({
    tagOpen,
    tagsInputFocused,
    selectedTags: tagAutocompleteValue,
  });

  const handleChangeComment = (id, content) => {
    setCommentText((prevState) => ({ ...prevState, innerHTML: content }));
  };
  const handleChangeSumary = (id, content) => {
    setCommentSummary((prevState) => ({ ...prevState, innerHTML: content }));
    setCopied(false);
  };

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  return (
    <>
      <Grid item xs={12} style={{ marginTop: "24px" }}>
        <FormControl variant="standard" className="forms-control">
          <label className="cl-new-comment-input-form-label" htmlFor="title">
            TITLE
          </label>
          <TextField
            id="cl_title"
            className="input-textfield"
            size="small"
            variant="outlined"
            value={commentTitle}
            onChange={(e) => setCommentTitle(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            type="text"
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#ccc" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#ccc" }
              },
            }}
          />
        </FormControl>
      </Grid>
      <Grid container>
        <Grid item xs={12} sm={5.75} className="cl-add-comment-sections">
          <Grid container className="input-controll">
            <Grid item xs={12} sm={12}>
              <FormControl variant="standard" className="forms-control">
                <label className="cl-new-comment-input-form-label" htmlFor="tags">
                  TAGS
                </label>
                <Autocomplete
                  className="forms-control-tag custom-color autocompleteCustom"
                  id="tags-outlined"
                  multiple
                  size="small"
                  options={tag}
                  value={tagAutocompleteValue}
                  onClick={(e) => e.stopPropagation()}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option?.name}
                  isOptionEqualToValue={(option, value) => option?.id != null && value?.id != null && String(option.id) === String(value.id)}
                  open={tagOpen}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#ccc" },
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#ccc" },
                    },
                  }}
                  onChange={(e, value) => setCommentTag(value)}
                  renderTags={(value, getTagProps) => {
                    const { visible, hiddenCount } = getTagChipVisibility(value, {
                      tagOpen,
                      tagsInputFocused,
                      collapsedVisibleTagCount,
                    });
                    return (
                      <>
                        {visible.map((option, index) => (
                          <Chip
                            variant="outlined"
                            size="small"
                            deleteIcon={<CloseIcon sx={{ color: `${getTextColor(option?.color)} !important`, fontSize: 16, backgroundColor: "transparent !important" }} />}
                            label={option?.name}
                            {...getTagProps({ index })}
                            sx={{
                              borderRadius: "35px",
                              border: "1px solid #CED2DA !important",
                              backgroundColor: `${option?.color} !important`,
                              color: `${getTextColor(option?.color)} !important`,
                              maxWidth: 120,
                              paddingLeft: "10px",
                            }}
                          />
                        ))}
                        {hiddenCount > 0 && (
                          <Box component="span" sx={{ ml: 0.5, fontSize: "14px", lineHeight: "24px", color: "#333333" }}>
                            {`+${hiddenCount}`}
                          </Box>
                        )}
                      </>
                    );
                  }}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
                      <Box
                        sx={{ width: "12px", height: "12px", backgroundColor: option?.color, borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center" }}
                        style={{ marginRight: 8 }}
                      />
                      {option?.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={commentTag?.length === 0 ? "Add tags" : ""}
                      onClick={!tagOpen ? handleTagOpen : handleTagClose}
                      onFocus={(e) => {
                        params.inputProps?.onFocus?.(e);
                        setTagsInputFocused(true);
                      }}
                      onBlur={(e) => {
                        params.inputProps?.onBlur?.(e);
                        setTagsInputFocused(false);
                        handleTagClose(e);
                      }}
                      InputProps={{
                        ...params.InputProps,
                        ref: mergeMuiOutlinedInputRef(tagsInputRootRef, params.InputProps.ref),
                        style: { fontFamily: "Open Sans!important", fontSize: "14px", fontWeight: 400, paddingLeft: commentTag?.length !== 0 ? "0px" : "20px" },
                        endAdornment: (
                          <>
                            <SearchIcon className={`customSearch ${commentTag?.length !== 0 ? "hidden" : ""}`} style={{ color: "#9b9b9b", marginRight: "8px" }} />
                            <SvgIcon className={`customArrowDown ${tagOpen ? "arrowActive" : ""}`} viewBox="" onClick={!tagOpen ? handleTagOpen : handleTagClose}>
                              {arrowDown}
                            </SvgIcon>
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </FormControl>
              <FormControl variant="standard" className="forms-control">
                <label id="commentText" className="cl-new-comment-input-form-label" htmlFor="comment" style={{ marginBottom: "9px" }}>
                  COMMENT
                </label>
                <FroalaEditorComponent setActiveEditorId={setActiveEditorId} onFocusEditor={() => { setActiveEditorId("commentText"); }}
                  setCommentOptionList={setCommentOptionList} commentOptionList={commentOptionList} templateId={props.templateId} type={"addComment"}
                  setCopied={setCopied} isEdit={false} categoryName={categoryName} categoryNameFromLocation={categoryNameFromLocation}
                  showAIModal={showAIModal} setShowAIModal={setShowAIModal} commentTitle={commentTitle}
                  setCommentText={setCommentText} showAI={storeData?.inspectionData?.data?.is_comment_helper_enable === 1 ? true : false}
                  editorData={commentText} commentEditorRef={commentEditor} editor={commentEditor} id={"commentText"} onChange={handleChangeComment}
                  froalaConfig={getFroalaConfig(storeData?.inspectionData?.data?.is_comment_helper_enable === 1)}
                  filterTagList={tag} categoryId={categoryId} />
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={0.5} display={"flex"} alignItems={"center"} justifyContent={"center"} pl={0.2} pt={14}>
          <IconButton onClick={handleCopyButton} className="summary-section-copy-icon" sx={{ "&:hover": { backgroundColor: "transparent" } }}>
            <SvgIcon viewBox="">{copyIcon}</SvgIcon>
          </IconButton>
        </Grid>
        <Grid item xs={12} sm={5.75} className="cl-add-comment-sections">
          <Grid container className="input-controll">
            <Grid item xs={12} sm={12}>
              <FormControl variant="standard" className="forms-control">
                <label className="cl-new-comment-input-form-label" htmlFor="title">
                  SUMMARY SECTION
                </label>
                <Autocomplete
                  className="forms-control-tag custom-color autocompleteSummaryCustom"
                  id="tags-outlined"
                  multiple
                  size="small"
                  limitTags={2}
                  options={summaryTag}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option?.name}
                  openOnFocus
                  onClick={(e) => e.stopPropagation()}
                  open={open}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#ccc" },
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#ccc" },
                    },
                  }}
                  onChange={(e, value) => {
                    setCommentSummaryTag(value);
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        className="custom-chip"
                        variant="outlined"
                        deleteIcon={<CloseIcon />}
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Box
                              sx={{
                                width: "12px",
                                height: "12px",
                                backgroundColor: option?.color,
                                borderRadius: "50%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                marginRight: "8px",
                              }}
                            />
                            <div style={{ fontSize: "14px" }}>{option?.name}</div>
                          </Box>
                        }
                        {...getTagProps({ index })}
                        sx={{ padding: "2px 2px 2px 10px !important", fontSize: "10px !important" }}
                      />
                    ))
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox style={{ borderRadius: "50%" }} icon={icon} checkedIcon={checkedIcon} checked={selected} />
                      <Box
                        sx={{ width: "12px", height: "12px", backgroundColor: option?.color, borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center" }}
                        style={{ marginRight: 8 }}
                      />
                      {option?.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={commentSummaryTag?.length === 0 ? "No summary selected" : ""}
                      onClick={!open ? handleOpen : handleClose}
                      onBlur={handleClose}
                      InputProps={{
                        ...params.InputProps,
                        style: { fontFamily: "Open Sans!important", fontSize: "14px", fontWeight: 400 },
                        endAdornment: (
                          <>
                            <SvgIcon className={`customArrowDown ${open ? "arrowActive" : ""}`} viewBox="" onClick={!open ? handleOpen : handleClose}>
                              {arrowDown}
                            </SvgIcon>
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </FormControl>
              <FormControl variant="standard" className="forms-control">
                <label id="summaryText" className="cl-new-comment-input-form-label" htmlFor="summay-comment" style={{ marginBottom: "9px" }}>
                  SUMMARY COMMENT
                </label>
                {!showAIModal ? <FroalaEditorComponent setActiveEditorId={setActiveEditorId}
                  onFocusEditor={() => { setActiveEditorId("summaryText"); }} commentOptionList={commentOptionList} templateId={props.templateId}
                  setCommentSummary={setCommentSummary} type={"addComment"} setCopied={setCopied} isEdit={false}
                  categoryName={categoryName} categoryNameFromLocation={categoryNameFromLocation}
                  showAIModal={showAIModal} setShowAIModal={setShowAIModal} showAI={false}
                  editorData={commentSummary} summaryEditorRef={editor} editor={editor} id={"summaryText"}
                  onChange={handleChangeSumary} froalaConfig={getFroalaConfig(false)}
                  copy={copied} filterTagList={tag} categoryId={categoryId} /> : null}
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {summaryCopyModalOpen && (
        <CopySummaryModal
          open={summaryCopyModalOpen}
          handleClose={setSummaryCopyModalOpen}
          handleSubmit={handleCopyComment}
          submitButtonTitle={"REPLACE"}
          dialogTitle={"Replace summary comment ?"}
          dialogContentText={"Are you sure you want to replace the summary comment ?"}
        />
      )}
    </>
  );
}

export default CLAddCommentModal;
